import { useCallback, useMemo, useRef } from "react";
import { errorLoggingService } from "@shared/services/errorLoggingService";
import logger from "@shared/utils/logger";

export interface PlaybackPerfSnapshot {
  frames: number;
  droppedFrames: number;
  droppedFrameRatio: number;
  slowFrameCount: number;
  maxFrameMs: number;
  avgFrameMs: number;
  transferSizeBytes: number;
  transferTimeMs: number;
  sessionTransferSizeBytes?: number;
  sessionTransferTimeMs?: number;
  /** Sprint 1 */
  maxAbsClockDriftMs: number;
  lastClockDriftMs: number;
  clockDriftOverToleranceCount: number;
  avgFrameBudgetRemainingMs: number;
  minFrameBudgetRemainingMs: number;
  seekCount: number;
  avgSeekLatencyMs: number;
  maxSeekLatencyMs: number;
  longTaskCount: number;
  maxLongTaskMs: number;
  /** Sprint 2 */
  tileCacheHitRate: number | null;
  tileCacheHitCount: number;
  tileCacheMissCount: number;
  tilePrefetchQueueDepth: number;
  staleTileDropCount: number;
  avgRenderWaitMs: number;
  maxRenderWaitMs: number;
  renderWaitCount: number;
  rendererMode?: "gpu" | "cpu";
  fallbackReason?: string;
  playbackSpeed?: number;
  resolutionLevel?: number;
  activeTab?: "historical" | "realtime";
  surface?: "eeg-only" | "eeg-dual-video";
  frameIntervalBuckets?: Record<string, number>;
  frameIntervalP95Ms?: number;
  degradationReasons?: string[];
}

type WindowPerfStore = {
  recordingId?: string;
  updatedAt?: string;
  snapshots?: PlaybackPerfSnapshot[];
  latest?: {
    clockDriftMs?: number;
    frameBudgetRemainingMs?: number;
    tileCacheHitRate?: number | null;
    tilePrefetchQueueDepth?: number;
    staleTileDropCount?: number;
    renderWaitMs?: number;
    overlayCount?: number;
    qeegLaneRenderCostMs?: number;
    frameIntervalP95Ms?: number | null;
    longTaskCount?: number;
    degradationReasons?: string[];
  };
};

const DROPPED_FRAME_THRESHOLD_MS = Number(
  import.meta.env.VITE_EEG_PLAYBACK_DROPPED_FRAME_THRESHOLD_MS || 50,
);
const SLOW_FRAME_THRESHOLD_MS = Number(
  import.meta.env.VITE_EEG_PLAYBACK_SLOW_FRAME_THRESHOLD_MS || 66,
);
const TILE_CACHE_MIN_SAMPLE_COUNT = Number(
  import.meta.env.VITE_EEG_PLAYBACK_TILE_CACHE_MIN_SAMPLE_COUNT || 12,
);

export function resolvePlaybackPerfSnapshotsForReset(params: {
  recordingId?: string;
  previousRecordingId?: string;
  existingSnapshots?: PlaybackPerfSnapshot[] | null;
}): PlaybackPerfSnapshot[] {
  const snapshots = Array.isArray(params.existingSnapshots)
    ? params.existingSnapshots
    : [];

  return params.previousRecordingId === params.recordingId
    ? snapshots.slice()
    : [];
}

interface PlaybackPerfOptions {
  rendererMode?: "gpu" | "cpu";
  fallbackReason?: string;
  playbackSpeed?: number;
  surface?: "eeg-only" | "eeg-dual-video";
  resolutionLevel?: number;
  activeTab?: "historical" | "realtime";
  dataWindowStart?: number;
}

export function usePlaybackPerf(
  recordingId?: string,
  options?: PlaybackPerfOptions,
) {
  const frameCountRef = useRef(0);
  const droppedFrameRef = useRef(0);
  const slowFrameCountRef = useRef(0);
  const maxFrameMsRef = useRef(0);
  const frameTimeTotalRef = useRef(0);
  const transferSizeRef = useRef(0);
  const transferTimeRef = useRef(0);
  const sessionTransferSizeRef = useRef(0);
  const sessionTransferTimeRef = useRef(0);
  const lastFlushRef = useRef(0);
  const lastBaselinePostRef = useRef(0);
  const lastDiagnosticAtRef = useRef(0);
  const lastDiagnosticSignatureRef = useRef("");

  // Sprint 1 timing
  const maxAbsClockDriftMsRef = useRef(0);
  const lastClockDriftMsRef = useRef(0);
  const clockDriftOverToleranceCountRef = useRef(0);
  const frameBudgetRemainingTotalRef = useRef(0);
  const frameBudgetSampleCountRef = useRef(0);
  const minFrameBudgetRemainingMsRef = useRef(Number.POSITIVE_INFINITY);
  const seekCountRef = useRef(0);
  const seekLatencyTotalRef = useRef(0);
  const maxSeekLatencyMsRef = useRef(0);
  const pendingSeekStartedAtMsRef = useRef<number | null>(null);
  const longTaskCountRef = useRef(0);
  const maxLongTaskMsRef = useRef(0);

  // Sprint 2 tile pipeline
  const tileCacheHitRateRef = useRef<number | null>(null);
  const tileCacheHitCountRef = useRef(0);
  const tileCacheMissCountRef = useRef(0);
  const tilePrefetchQueueDepthRef = useRef(0);
  const staleTileDropCountRef = useRef(0);
  const renderWaitTotalRef = useRef(0);
  const renderWaitCountRef = useRef(0);
  const maxRenderWaitMsRef = useRef(0);
  const renderWaitStartedAtMsRef = useRef<number | null>(null);
  const frameIntervalBucketsRef = useRef({
    le16: 0,
    le33: 0,
    le50: 0,
    le100: 0,
    le200: 0,
    gt200: 0,
  });
  const frameIntervalSamplesRef = useRef<number[]>([]);

  const diagnosticsEnabled = useMemo(() => {
    const explicit = String(
      import.meta.env.VITE_EEG_PLAYBACK_DIAGNOSTICS || "",
    ).toLowerCase();
    if (explicit === "true") return true;
    if (explicit === "false") return false;
    return import.meta.env.DEV;
  }, []);

  const publishLatest = useCallback(
    (partial: NonNullable<WindowPerfStore["latest"]>) => {
      if (typeof window === "undefined") return;
      const existing = ((window as any).__eegPlaybackPerf ||
        {}) as WindowPerfStore;
      (window as any).__eegPlaybackPerf = {
        ...existing,
        recordingId,
        updatedAt: new Date().toISOString(),
        latest: {
          ...(existing.latest || {}),
          ...partial,
        },
      } as WindowPerfStore;
    },
    [recordingId],
  );

  const recordFrame = useCallback((deltaMs: number) => {
    frameCountRef.current += 1;
    // Clamp deltas from tab-visibility/backgrounding gaps so a single huge
    // gap (e.g. 800ms React scheduler stall) doesn't permanently inflate metrics.
    const clampedDelta = Math.min(deltaMs, 500);
    frameTimeTotalRef.current += clampedDelta;
    maxFrameMsRef.current = Math.max(maxFrameMsRef.current, clampedDelta);
    // Dropped = clinically visible hitch, not a healthy 30fps cadence.
    // Browser RAF under CPU/load often lands at 33.3-34ms; counting that as a
    // dropped frame created false "bumpy playback" failures during stable 30fps
    // review. Keep true hitch signals through >50ms, p95, max gap, and long-task
    // telemetry.
    if (clampedDelta > DROPPED_FRAME_THRESHOLD_MS) {
      droppedFrameRef.current += 1;
    }
    if (clampedDelta > SLOW_FRAME_THRESHOLD_MS) {
      slowFrameCountRef.current += 1;
    }
    if (clampedDelta <= 16) frameIntervalBucketsRef.current.le16 += 1;
    else if (clampedDelta <= 33) frameIntervalBucketsRef.current.le33 += 1;
    else if (clampedDelta <= 50) frameIntervalBucketsRef.current.le50 += 1;
    else if (clampedDelta <= 100) frameIntervalBucketsRef.current.le100 += 1;
    else if (clampedDelta <= 200) frameIntervalBucketsRef.current.le200 += 1;
    else frameIntervalBucketsRef.current.gt200 += 1;
    frameIntervalSamplesRef.current.push(clampedDelta);
    if (frameIntervalSamplesRef.current.length > 600) {
      frameIntervalSamplesRef.current.splice(
        0,
        frameIntervalSamplesRef.current.length - 600,
      );
    }
  }, []);

  const recordTransfer = useCallback(
    (sizeBytes: number, loadTimeMs: number) => {
      const safeSize = Number.isFinite(sizeBytes) ? Math.max(0, sizeBytes) : 0;
      const safeTime = Number.isFinite(loadTimeMs) ? Math.max(0, loadTimeMs) : 0;
      transferSizeRef.current += safeSize;
      transferTimeRef.current += safeTime;
      sessionTransferSizeRef.current += safeSize;
      sessionTransferTimeRef.current += safeTime;
    },
    [],
  );

  const recordTimingSample = useCallback(
    (sample: {
      driftMs: number;
      exceedsTolerance: boolean;
      frameBudgetRemainingMs: number;
    }) => {
      lastClockDriftMsRef.current = sample.driftMs;
      maxAbsClockDriftMsRef.current = Math.max(
        maxAbsClockDriftMsRef.current,
        sample.driftMs,
      );
      if (sample.exceedsTolerance) {
        clockDriftOverToleranceCountRef.current += 1;
      }
      frameBudgetRemainingTotalRef.current += sample.frameBudgetRemainingMs;
      frameBudgetSampleCountRef.current += 1;
      minFrameBudgetRemainingMsRef.current = Math.min(
        minFrameBudgetRemainingMsRef.current,
        sample.frameBudgetRemainingMs,
      );
      publishLatest({
        clockDriftMs: Number(sample.driftMs.toFixed(2)),
        frameBudgetRemainingMs: Number(sample.frameBudgetRemainingMs.toFixed(2)),
      });
    },
    [publishLatest],
  );

  const recordSeekRequest = useCallback(() => {
    pendingSeekStartedAtMsRef.current = performance.now();
  }, []);

  const recordSeekComplete = useCallback(() => {
    const startedAt = pendingSeekStartedAtMsRef.current;
    if (startedAt === null) return;
    const latencyMs = Math.max(0, performance.now() - startedAt);
    pendingSeekStartedAtMsRef.current = null;
    seekCountRef.current += 1;
    seekLatencyTotalRef.current += latencyMs;
    maxSeekLatencyMsRef.current = Math.max(
      maxSeekLatencyMsRef.current,
      latencyMs,
    );
  }, []);

  const recordLongTask = useCallback((durationMs: number) => {
    const clamped = Math.min(Math.max(0, durationMs), 5000);
    longTaskCountRef.current += 1;
    maxLongTaskMsRef.current = Math.max(maxLongTaskMsRef.current, clamped);
  }, []);

  const recordTileCacheStats = useCallback(
    (stats: {
      hitRate: number;
      hitCount: number;
      missCount: number;
      queueDepth?: number;
      staleDropCount?: number;
    }) => {
      tileCacheHitRateRef.current = stats.hitRate;
      tileCacheHitCountRef.current = stats.hitCount;
      tileCacheMissCountRef.current = stats.missCount;
      if (typeof stats.queueDepth === "number") {
        tilePrefetchQueueDepthRef.current = stats.queueDepth;
      }
      if (typeof stats.staleDropCount === "number") {
        staleTileDropCountRef.current = stats.staleDropCount;
      }
      publishLatest({
        tileCacheHitRate: stats.hitRate,
        tilePrefetchQueueDepth: tilePrefetchQueueDepthRef.current,
        staleTileDropCount: staleTileDropCountRef.current,
      });
    },
    [publishLatest],
  );

  const beginRenderWait = useCallback(() => {
    if (renderWaitStartedAtMsRef.current === null) {
      renderWaitStartedAtMsRef.current = performance.now();
    }
  }, []);

  const endRenderWait = useCallback(() => {
    const startedAt = renderWaitStartedAtMsRef.current;
    if (startedAt === null) return;
    const waitMs = Math.max(0, performance.now() - startedAt);
    renderWaitStartedAtMsRef.current = null;
    renderWaitCountRef.current += 1;
    renderWaitTotalRef.current += waitMs;
    maxRenderWaitMsRef.current = Math.max(maxRenderWaitMsRef.current, waitMs);
    publishLatest({ renderWaitMs: Number(waitMs.toFixed(1)) });
  }, [publishLatest]);

  const getSnapshot = useCallback((): PlaybackPerfSnapshot => {
    const frames = frameCountRef.current;
    const droppedFrames = droppedFrameRef.current;
    const seekCount = seekCountRef.current;
    const renderWaitCount = renderWaitCountRef.current;
    const budgetSamples = frameBudgetSampleCountRef.current;
    const sortedFrameIntervals = frameIntervalSamplesRef.current
      .slice()
      .sort((a, b) => a - b);
    const p95Index =
      sortedFrameIntervals.length > 0
        ? Math.min(
            sortedFrameIntervals.length - 1,
            Math.floor(sortedFrameIntervals.length * 0.95),
          )
        : -1;
    return {
      frames,
      droppedFrames,
      droppedFrameRatio: frames > 0 ? droppedFrames / frames : 0,
      slowFrameCount: slowFrameCountRef.current,
      maxFrameMs: maxFrameMsRef.current,
      avgFrameMs: frames > 0 ? frameTimeTotalRef.current / frames : 0,
      transferSizeBytes: transferSizeRef.current,
      transferTimeMs: transferTimeRef.current,
      sessionTransferSizeBytes: sessionTransferSizeRef.current,
      sessionTransferTimeMs: sessionTransferTimeRef.current,
      maxAbsClockDriftMs: maxAbsClockDriftMsRef.current,
      lastClockDriftMs: lastClockDriftMsRef.current,
      clockDriftOverToleranceCount: clockDriftOverToleranceCountRef.current,
      avgFrameBudgetRemainingMs:
        budgetSamples > 0
          ? frameBudgetRemainingTotalRef.current / budgetSamples
          : 0,
      minFrameBudgetRemainingMs: Number.isFinite(
        minFrameBudgetRemainingMsRef.current,
      )
        ? minFrameBudgetRemainingMsRef.current
        : 0,
      seekCount,
      avgSeekLatencyMs:
        seekCount > 0 ? seekLatencyTotalRef.current / seekCount : 0,
      maxSeekLatencyMs: maxSeekLatencyMsRef.current,
      longTaskCount: longTaskCountRef.current,
      maxLongTaskMs: maxLongTaskMsRef.current,
      tileCacheHitRate: tileCacheHitRateRef.current,
      tileCacheHitCount: tileCacheHitCountRef.current,
      tileCacheMissCount: tileCacheMissCountRef.current,
      tilePrefetchQueueDepth: tilePrefetchQueueDepthRef.current,
      staleTileDropCount: staleTileDropCountRef.current,
      avgRenderWaitMs:
        renderWaitCount > 0
          ? renderWaitTotalRef.current / renderWaitCount
          : 0,
      maxRenderWaitMs: maxRenderWaitMsRef.current,
      renderWaitCount,
      rendererMode: options?.rendererMode,
      fallbackReason: options?.fallbackReason,
      playbackSpeed: options?.playbackSpeed,
      resolutionLevel: options?.resolutionLevel,
      activeTab: options?.activeTab,
      surface: options?.surface,
      frameIntervalBuckets: { ...frameIntervalBucketsRef.current },
      frameIntervalP95Ms:
        p95Index >= 0 ? sortedFrameIntervals[p95Index] : undefined,
    };
  }, [
    options?.activeTab,
    options?.fallbackReason,
    options?.playbackSpeed,
    options?.rendererMode,
    options?.resolutionLevel,
    options?.surface,
  ]);

  const reset = useCallback(() => {
    frameCountRef.current = 0;
    droppedFrameRef.current = 0;
    slowFrameCountRef.current = 0;
    maxFrameMsRef.current = 0;
    frameTimeTotalRef.current = 0;
    transferSizeRef.current = 0;
    transferTimeRef.current = 0;
    sessionTransferSizeRef.current = 0;
    sessionTransferTimeRef.current = 0;
    lastFlushRef.current = 0;
    lastBaselinePostRef.current = 0;
    lastDiagnosticAtRef.current = 0;
    lastDiagnosticSignatureRef.current = "";
    maxAbsClockDriftMsRef.current = 0;
    lastClockDriftMsRef.current = 0;
    clockDriftOverToleranceCountRef.current = 0;
    frameBudgetRemainingTotalRef.current = 0;
    frameBudgetSampleCountRef.current = 0;
    minFrameBudgetRemainingMsRef.current = Number.POSITIVE_INFINITY;
    seekCountRef.current = 0;
    seekLatencyTotalRef.current = 0;
    maxSeekLatencyMsRef.current = 0;
    pendingSeekStartedAtMsRef.current = null;
    longTaskCountRef.current = 0;
    maxLongTaskMsRef.current = 0;
    tileCacheHitRateRef.current = null;
    tileCacheHitCountRef.current = 0;
    tileCacheMissCountRef.current = 0;
    tilePrefetchQueueDepthRef.current = 0;
    staleTileDropCountRef.current = 0;
    renderWaitTotalRef.current = 0;
    renderWaitCountRef.current = 0;
    maxRenderWaitMsRef.current = 0;
    renderWaitStartedAtMsRef.current = null;
    frameIntervalBucketsRef.current = {
      le16: 0,
      le33: 0,
      le50: 0,
      le100: 0,
      le200: 0,
      gt200: 0,
    };
    frameIntervalSamplesRef.current = [];

    if (typeof window !== "undefined") {
      const existingStore = ((window as any).__eegPlaybackPerf ||
        null) as WindowPerfStore | null;
      (window as any).__eegPlaybackPerf = {
        recordingId,
        updatedAt: new Date().toISOString(),
        snapshots: resolvePlaybackPerfSnapshotsForReset({
          recordingId,
          previousRecordingId: existingStore?.recordingId,
          existingSnapshots: existingStore?.snapshots,
        }),
        latest: {},
      } as WindowPerfStore;
    }
  }, [recordingId]);

  const flushIfDue = useCallback(
    (force = false) => {
      const now = Date.now();
      if (!force) {
        if (lastFlushRef.current === 0) {
          lastFlushRef.current = now;
          return;
        }
        if (now - lastFlushRef.current < 10000) {
          return;
        }
      }
      lastFlushRef.current = now;

      const snapshot = getSnapshot();

      // Reset window counters so the next 10 s snapshot is independent of prior
      // windows.  Without this, startup spikes (IDB block, cold-cache renders,
      // API response storms) permanently inflate droppedFrameRatio and keep
      // `isDegraded` true even when steady-state performance is fine.
      frameCountRef.current = 0;
      droppedFrameRef.current = 0;
      slowFrameCountRef.current = 0;
      maxFrameMsRef.current = 0;
      frameTimeTotalRef.current = 0;
      frameIntervalBucketsRef.current = {
        le16: 0,
        le33: 0,
        le50: 0,
        le100: 0,
        le200: 0,
        gt200: 0,
      };
      frameIntervalSamplesRef.current = [];
      maxAbsClockDriftMsRef.current = 0;
      clockDriftOverToleranceCountRef.current = 0;
      frameBudgetRemainingTotalRef.current = 0;
      frameBudgetSampleCountRef.current = 0;
      minFrameBudgetRemainingMsRef.current = Number.POSITIVE_INFINITY;
      longTaskCountRef.current = 0;
      maxLongTaskMsRef.current = 0;
      renderWaitTotalRef.current = 0;
      renderWaitCountRef.current = 0;
      maxRenderWaitMsRef.current = 0;
      transferSizeRef.current = 0;
      transferTimeRef.current = 0;

      errorLoggingService.addPlaybackTelemetry({
        recordingId,
        ...snapshot,
      });

      const playbackSpeed = snapshot.playbackSpeed || 1;
      const isHighSpeed = playbackSpeed >= 100;
      const isRapidReview = playbackSpeed >= 16 && playbackSpeed < 100;
      const isDualVideo = (options?.surface || "eeg-only") === "eeg-dual-video";
      const isUltraHighSpeedSingleSurface = isHighSpeed && !isDualVideo;
      const transferMbps =
        snapshot.transferTimeMs > 0
          ? (snapshot.transferSizeBytes * 8) /
            (snapshot.transferTimeMs / 1000) /
            1_000_000
          : 0;
      const highSpeedAvgThresholdMs = isUltraHighSpeedSingleSurface ? 33 : 20;
      const highSpeedDroppedThreshold = isUltraHighSpeedSingleSurface
        ? 0.3
        : 0.2;
      const highSpeedMaxThresholdMs = isUltraHighSpeedSingleSurface ? 200 : 120;
      const degradedHighSpeed =
        isHighSpeed &&
        (snapshot.avgFrameMs > highSpeedAvgThresholdMs ||
          snapshot.droppedFrameRatio > highSpeedDroppedThreshold ||
          snapshot.maxFrameMs > highSpeedMaxThresholdMs);
      const degradedDualVideo =
        isDualVideo &&
        (snapshot.avgFrameMs > 24 ||
          snapshot.droppedFrameRatio > 0.25 ||
          snapshot.maxFrameMs > 140);
      // 16–60x blit cadence is often 33–55ms. The clinical 33ms/0.35 dropped
      // thresholds logged `degraded_playback_detected` every 10s for a full
      // 20m 60x segment (~120 console warnings) without a soak fail.
      const rapidReviewAvgThresholdMs = 66;
      const rapidReviewDroppedThreshold = 0.7;
      const rapidReviewMaxThresholdMs = 250;
      const degradedRapidReview =
        isRapidReview &&
        !isDualVideo &&
        (snapshot.avgFrameMs > rapidReviewAvgThresholdMs ||
          snapshot.droppedFrameRatio > rapidReviewDroppedThreshold ||
          snapshot.maxFrameMs > rapidReviewMaxThresholdMs);
      const degradedNormal =
        !isHighSpeed &&
        !isRapidReview &&
        !isDualVideo &&
        (snapshot.avgFrameMs > 33 || snapshot.droppedFrameRatio > 0.35);
      const isDegraded =
        degradedHighSpeed ||
        degradedRapidReview ||
        degradedDualVideo ||
        degradedNormal;
      const avgThresholdMs = isHighSpeed
        ? highSpeedAvgThresholdMs
        : isRapidReview
          ? rapidReviewAvgThresholdMs
          : isDualVideo
            ? 24
            : 33;
      const droppedThreshold = isHighSpeed
        ? highSpeedDroppedThreshold
        : isRapidReview
          ? rapidReviewDroppedThreshold
          : isDualVideo
            ? 0.25
            : 0.35;
      const maxThresholdMs = isHighSpeed
        ? highSpeedMaxThresholdMs
        : isRapidReview
          ? rapidReviewMaxThresholdMs
          : isDualVideo
            ? 140
            : 500;
      const tileCacheSamples =
        snapshot.tileCacheHitCount + snapshot.tileCacheMissCount;
      const cacheWarmEnough = tileCacheSamples >= TILE_CACHE_MIN_SAMPLE_COUNT;
      const degradationReasons = [
        snapshot.avgFrameMs > avgThresholdMs ? "avg_frame" : null,
        snapshot.droppedFrameRatio > droppedThreshold ? "dropped_frames" : null,
        snapshot.maxFrameMs > maxThresholdMs ? "max_frame" : null,
        snapshot.longTaskCount > 0 ? "long_task" : null,
        snapshot.maxRenderWaitMs > 50 ? "render_wait" : null,
        cacheWarmEnough &&
        snapshot.tileCacheHitRate !== null &&
        snapshot.tileCacheHitRate < 0.75
          ? "tile_cache_miss"
          : null,
        snapshot.staleTileDropCount > 0 ? "stale_tile_drop" : null,
        Math.abs(snapshot.maxAbsClockDriftMs) > 1000 ? "clock_drift" : null,
      ].filter((reason): reason is string => Boolean(reason));
      snapshot.degradationReasons = degradationReasons;

      const diagnosticPayload = {
        recordingId,
        rendererMode: snapshot.rendererMode || "unknown",
        fallbackReason: snapshot.fallbackReason || "none",
        speed: snapshot.playbackSpeed || 1,
        surface: options?.surface || "eeg-only",
        resolutionLevel: options?.resolutionLevel ?? null,
        activeTab: options?.activeTab || "historical",
        dataWindowStart: options?.dataWindowStart ?? null,
        avgFrameMs: Number(snapshot.avgFrameMs.toFixed(2)),
        maxFrameMs: Number(snapshot.maxFrameMs.toFixed(2)),
        droppedFrameRatio: Number(snapshot.droppedFrameRatio.toFixed(3)),
        slowFrameCount: snapshot.slowFrameCount,
        transferSizeBytes: snapshot.transferSizeBytes,
        transferTimeMs: Number(snapshot.transferTimeMs.toFixed(1)),
        sessionTransferSizeBytes: snapshot.sessionTransferSizeBytes,
        sessionTransferTimeMs: Number(
          (snapshot.sessionTransferTimeMs ?? 0).toFixed(1),
        ),
        transferMbps: Number(transferMbps.toFixed(2)),
        lastClockDriftMs: Number(snapshot.lastClockDriftMs.toFixed(2)),
        maxAbsClockDriftMs: Number(snapshot.maxAbsClockDriftMs.toFixed(2)),
        tileCacheHitRate:
          snapshot.tileCacheHitRate === null
            ? null
            : Number(snapshot.tileCacheHitRate.toFixed(3)),
        tileCacheHitCount: snapshot.tileCacheHitCount,
        tileCacheMissCount: snapshot.tileCacheMissCount,
        tileCacheSamples,
        tileCacheWarmEnough: cacheWarmEnough,
        tilePrefetchQueueDepth: snapshot.tilePrefetchQueueDepth,
        staleTileDropCount: snapshot.staleTileDropCount,
        avgRenderWaitMs: Number(snapshot.avgRenderWaitMs.toFixed(1)),
        maxRenderWaitMs: Number(snapshot.maxRenderWaitMs.toFixed(1)),
        longTaskCount: snapshot.longTaskCount,
        maxLongTaskMs: Number(snapshot.maxLongTaskMs.toFixed(1)),
        frameIntervalP95Ms:
          snapshot.frameIntervalP95Ms == null
            ? null
            : Number(snapshot.frameIntervalP95Ms.toFixed(1)),
        frameIntervalBuckets: snapshot.frameIntervalBuckets,
        degradationReasons,
      };

      if (diagnosticsEnabled) {
        const signature = [
          diagnosticPayload.rendererMode,
          diagnosticPayload.speed,
          diagnosticPayload.surface,
          diagnosticPayload.resolutionLevel,
          Math.round(diagnosticPayload.avgFrameMs),
          Math.round(diagnosticPayload.maxFrameMs),
          Math.round(diagnosticPayload.droppedFrameRatio * 100),
        ].join("|");
        const sinceLastDiag = now - lastDiagnosticAtRef.current;
        const shouldLogWarn =
          isDegraded &&
          (signature !== lastDiagnosticSignatureRef.current ||
            sinceLastDiag >= 10_000);
        const shouldLogInfo =
          !isDegraded &&
          (isHighSpeed || isDualVideo) &&
          sinceLastDiag >= 30_000;

        if (shouldLogWarn) {
          logger.warn(
            `[EEGPlaybackPerf] degraded_playback_detected avg=${diagnosticPayload.avgFrameMs}ms max=${diagnosticPayload.maxFrameMs}ms dropped=${(diagnosticPayload.droppedFrameRatio * 100).toFixed(1)}% slow=${diagnosticPayload.slowFrameCount}`,
            diagnosticPayload,
          );
          errorLoggingService.addBreadcrumb(
            "eeg_playback_perf_degraded",
            "eeg-playback",
            diagnosticPayload,
          );
          lastDiagnosticAtRef.current = now;
          lastDiagnosticSignatureRef.current = signature;
        } else if (shouldLogInfo) {
          logger.info(
            "[EEGPlaybackPerf] high_speed_diagnostics",
            diagnosticPayload,
          );
          lastDiagnosticAtRef.current = now;
          lastDiagnosticSignatureRef.current = signature;
        }
      }

      if (typeof window !== "undefined") {
        const perfStore = ((window as any).__eegPlaybackPerf ||
          {}) as WindowPerfStore;
        const snapshots = perfStore.snapshots || [];
        snapshots.push(snapshot);
        while (snapshots.length > 100) {
          snapshots.shift();
        }
        (window as any).__eegPlaybackPerf = {
          ...perfStore,
          recordingId,
          updatedAt: new Date().toISOString(),
          snapshots,
          latest: {
            ...(perfStore.latest || {}),
            clockDriftMs: snapshot.lastClockDriftMs,
            frameBudgetRemainingMs: snapshot.avgFrameBudgetRemainingMs,
            tileCacheHitRate: snapshot.tileCacheHitRate,
            tilePrefetchQueueDepth: snapshot.tilePrefetchQueueDepth,
            staleTileDropCount: snapshot.staleTileDropCount,
            renderWaitMs: snapshot.avgRenderWaitMs,
            frameIntervalP95Ms: snapshot.frameIntervalP95Ms,
            longTaskCount: snapshot.longTaskCount,
            degradationReasons: snapshot.degradationReasons || [],
          },
        } as WindowPerfStore;

        try {
          const existing = JSON.parse(
            localStorage.getItem("eegPlaybackBaseline") || "[]",
          ) as Array<
            PlaybackPerfSnapshot & { recordingId?: string; capturedAt?: string }
          >;
          existing.push({
            ...snapshot,
            recordingId,
            capturedAt: new Date().toISOString(),
          });
          while (existing.length > 500) existing.shift();
          localStorage.setItem("eegPlaybackBaseline", JSON.stringify(existing));
        } catch {
          // Non-fatal local persistence best effort.
        }

        if (
          (snapshot.rendererMode === "cpu" || isHighSpeed || isDualVideo) &&
          now - lastBaselinePostRef.current >= 10000
        ) {
          lastBaselinePostRef.current = now;
          const perfLatest =
            typeof window !== "undefined"
              ? (((window as any).__eegPlaybackPerf || {}) as WindowPerfStore)
                  .latest
              : undefined;
          import("@shared/services/apiClient")
            .then(({ default: apiClient }) => {
              apiClient.post("/eeg/playback/baseline", {
                recordedAt: new Date().toISOString(),
                recordingId,
                scenario: `${snapshot.rendererMode || "unknown"}-${
                  isDualVideo ? "dual-video" : "eeg"
                }-${Math.round(snapshot.playbackSpeed || 1)}x`,
                surface: options?.surface || "eeg-only",
                speed: snapshot.playbackSpeed || 1,
                renderer_mode: snapshot.rendererMode,
                fallback_reason: snapshot.fallbackReason || "none",
                frames: snapshot.frames,
                droppedFrames: snapshot.droppedFrames,
                avgFrameMs: snapshot.avgFrameMs,
                transferSizeBytes: snapshot.transferSizeBytes,
                transferTimeMs: snapshot.transferTimeMs,
                maxFrameMs: snapshot.maxFrameMs,
                droppedFrameRatio: snapshot.droppedFrameRatio,
                slowFrameCount: snapshot.slowFrameCount,
                resolutionLevel: options?.resolutionLevel,
                lastClockDriftMs: snapshot.lastClockDriftMs,
                tileCacheHitRate: snapshot.tileCacheHitRate,
                tilePrefetchQueueDepth: snapshot.tilePrefetchQueueDepth,
                staleTileDropCount: snapshot.staleTileDropCount,
                frameIntervalP95Ms: snapshot.frameIntervalP95Ms,
                longTaskCount: snapshot.longTaskCount,
                maxLongTaskMs: snapshot.maxLongTaskMs,
                overlayCount: Number(perfLatest?.overlayCount || 0),
                qeegLaneRenderCostMs: Number(
                  perfLatest?.qeegLaneRenderCostMs || 0,
                ),
                degradationReasons: snapshot.degradationReasons || [],
              });
            })
            .catch(() => {
              // Best effort only - must not interrupt playback loop.
            });
        }
      }
    },
    [
      diagnosticsEnabled,
      getSnapshot,
      options?.activeTab,
      options?.dataWindowStart,
      options?.resolutionLevel,
      options?.surface,
      recordingId,
    ],
  );

  return useMemo(
    () => ({
      recordFrame,
      recordTransfer,
      recordTimingSample,
      recordSeekRequest,
      recordSeekComplete,
      recordLongTask,
      recordTileCacheStats,
      beginRenderWait,
      endRenderWait,
      getSnapshot,
      flushIfDue,
      reset,
    }),
    [
      recordFrame,
      recordTransfer,
      recordTimingSample,
      recordSeekRequest,
      recordSeekComplete,
      recordLongTask,
      recordTileCacheStats,
      beginRenderWait,
      endRenderWait,
      getSnapshot,
      flushIfDue,
      reset,
    ],
  );
}

export default usePlaybackPerf;
